/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useState } from "react";
import { Card, Typography, Space, Tag, Divider, Button } from "antd";
import {
  ClockCircleOutlined,
  CarOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  ScheduleOutlined,
  ReconciliationOutlined,
  ExpandOutlined,
} from "@ant-design/icons";
import moment from "moment";
import PropTypes from "prop-types";
import "./TrasladoItem.css";
import ObservacionesModal from "../../common/ObservacionesModal";

const { Text } = Typography;

const TrasladoItem = ({ traslado, onSelect, statusColor }) => {
  const [modalVisible, setModalVisible] = useState(false);

  const getStatusColor = (status) => {
    const statusColors = {
      Cancelado: "#b10e1a",
      Finalizado: "#4d4d4d",
      Fallido: "#4a2c04",
      FallidoXvalidar: "#d4380d",
      Asignado: "#1f7362",
      "Pre-Asignado": "#0c27a0",
      Programado: "#6c06b5",
      Enterado: "#0c5b07",
      Iniciado: "#fa7114",
      Aceptado: "#088A08",
      "En Camino": "#FFBF00",
      Esperando: "#a44706",
      "Inicio Exitosamente": "#52c41a",
      "Finalizó Exitosamente": "#52c41a",
      default: "#b0b500",
    };
    return statusColors[status] || statusColors.default;
  };

  return (
    <Card
      className="traslado-card"
      onClick={() => onSelect(traslado)}
      hoverable
      style={{ width: "100%" }}
    >
      {/* Encabezado con color de fondo según statusColor */}
      <div className="card-header" style={{ backgroundColor: statusColor }}>
        <Text strong className="header-text">
          CS-{traslado.id}
        </Text>
        <Tag color={getStatusColor(traslado.status)} className="header-tag">
          {traslado.status}
        </Tag>
      </div>

      {/* Cuerpo de la tarjeta con secciones agrupadas */}
      <div className="card-body">
        <Space direction="vertical" size="small">
          {/* Sección de información primaria */}
          <div className="primary-info">
            <Text className="info-text">
              <ClockCircleOutlined className="mobile-icon" />{" "}
              {moment(traslado.fecha).format("DD/MM/YYYY")} -{" "}
              {traslado.hora_recogida.slice(0, 5)} -{" "}
              {traslado.hora_llegada.slice(0, 5)}
            </Text>
            {/* Solo llegan en la vista del administrador de vehículos (varios vehículos y conductores) */}
            {(traslado.vehiculo || traslado.conductor) && (
              <Text className="info-text">
                <CarOutlined className="mobile-icon" />{" "}
                {[traslado.vehiculo?.placa, traslado.conductor?.nombre].filter(Boolean).join(" - ")}
              </Text>
            )}
          </div>

          <Divider style={{ margin: "8px 0" }} />

          {/* Sección de información de ubicación */}
          <div className="location-info">
            <Text className="info-text">
              <EnvironmentOutlined className="mobile-icon" />
              {traslado.origen_lugar} - {traslado.origen_direccion} -{" "}
              {traslado.origen_barrio} -{traslado.origen_ciudad}
            </Text>
            <Text className="info-text">
              <EnvironmentOutlined className="mobile-icon" />
              {traslado.destino_lugar} - {traslado.destino_direccion} -{" "}
              {traslado.destino_barrio} - {traslado.destino_ciudad}
            </Text>
          </div>

          <Divider style={{ margin: "8px 0" }} />

          {/* Sección de información de paciente y pago */}
          <div className="patient-payment-info">
            <Text className="info-text" style={{ fontWeight: 500 }}>
              {traslado.nombre_paciente}
            </Text>
            {traslado.numero_contacto_paciente && (
              <Text className="info-text">
                <PhoneOutlined className="mobile-icon" />{" "}
                {traslado.numero_contacto_paciente}
              </Text>
            )}
            {traslado.numero_documento_paciente && (
              <Text className="info-text">
                <ScheduleOutlined className="mobile-icon" />{" "}
                {traslado.numero_documento_paciente}
              </Text>
            )}
            {traslado.tipo_documento_paciente && (
              <Text className="info-text">
                <ScheduleOutlined className="mobile-icon" />{" "}
                {traslado.tipo_documento_paciente}
              </Text>
            )}
            {traslado.empresa && (
              <Text className="info-text">
                <ReconciliationOutlined className="mobile-icon" />{" "}
                {traslado.empresa}
              </Text>
            )}
            {traslado.copago_diario && (
              <Text className="info-text">
                <DollarOutlined className="mobile-icon" />{" "}
                {traslado.copago_diario}
              </Text>
            )}
          </div>

          <Divider style={{ margin: "8px 0" }} />

          {/* Sección de indicaciones adicionales */}
          <div className="indications-info">
            {traslado.indicaciones_conductor && (
              <>
                <Text
                  className="info-text"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  <InfoCircleOutlined className="mobile-icon" />{" "}
                  {traslado.indicaciones_conductor}
                </Text>
                {traslado.indicaciones_conductor.length > 80 && (
                  <Button
                    type="link"
                    size="small"
                    icon={<ExpandOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalVisible(true);
                    }}
                    style={{ padding: 0, marginTop: 4, height: "auto" }}
                  >
                    Ver todo
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Etiquetas de inicio y fin */}
          <div className="status-tags">
            {traslado.inicio_status && (
              <Tag
                color={getStatusColor(traslado.inicio_status)}
                className="status-tag"
              >
                {traslado.inicio_status}
              </Tag>
            )}
            {traslado.fin_status && (
              <Tag
                color={getStatusColor(traslado.fin_status)}
                className="status-tag"
              >
                {traslado.fin_status}
              </Tag>
            )}
          </div>
        </Space>
      </div>

      {/* Modal para ver indicaciones completas */}
      {traslado.indicaciones_conductor && (
        <ObservacionesModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          titulo="Indicaciones del Conductor"
          contenido={traslado.indicaciones_conductor}
          icono={InfoCircleOutlined}
          iconColor="#1890ff"
        />
      )}
    </Card>
  );
};

TrasladoItem.propTypes = {
  traslado: PropTypes.shape({
    id: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    fecha: PropTypes.string.isRequired,
    hora_recogida: PropTypes.string.isRequired,
    hora_llegada: PropTypes.string,
    origen_lugar: PropTypes.string,
    origen_direccion: PropTypes.string,
    origen_ciudad: PropTypes.string,
    destino_lugar: PropTypes.string,
    destino_direccion: PropTypes.string,
    destino_ciudad: PropTypes.string,
    nombre_paciente: PropTypes.string,
    numero_contacto_paciente: PropTypes.string,
    inicio_status: PropTypes.string,
    fin_status: PropTypes.string,
    copago: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    copago_diario: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    indicaciones_conductor: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  statusColor: PropTypes.string.isRequired,
};

export default TrasladoItem;
